import React, { useCallback, useMemo } from 'react';

import { GLOBAL_HEADER_MEDIA } from '../../../../constants/templates';
import { MAX_IMAGE_SIZE_BYTES, MAX_IMAGE_SIZE_BYTES_MB, VISUAL_MEDIA_TYPES } from '../../../../constants/upload';
import { useTemplateUploadHandlers } from '../../../../hooks/useTemplateUploadHandlers';
import { IBaseUploadControlProps, IImageUrlValueProps } from '../../../../types/templates';
import { ResetPlaceholder } from '../../_controls_';
import { ImageUploader } from '../FileUploader/ImageUploader';
import { FormLabel } from '../FormLabel';

interface IProps extends IBaseUploadControlProps, IImageUrlValueProps {
  onDrop?: (files: File[]) => void;
  onUpload?: (url: string) => void;
  onRemove: () => void;
  onReset?: () => void;
  onClearOverride?: () => void;
  label?: string;
}

export const HeaderMediaUploader: React.FC<IProps> = ({
  mode,
  globalValue,
  overrideValue,
  defaultValue,
  onDrop: onDropProp,
  onUpload,
  onRemove,
  onReset,
  onClearOverride,
  label = 'Hero media',
  dataAction,
}) => {
  const { isPageOverrideMode, isOverridden, shouldClearOverride, handleDrop, handleRemoveClick } =
    useTemplateUploadHandlers({
      mode,
      overrideValue,
      fieldName: GLOBAL_HEADER_MEDIA,
      onDrop: onDropProp,
      onUpload,
      onRemove,
      onClearOverride,
    });

  const displayUrl = useMemo(() => {
    if (isOverridden) {
      return overrideValue;
    }
    return globalValue || '';
  }, [isOverridden, overrideValue, globalValue]);

  const isCustomized = useMemo(() => {
    const currentUrl = isOverridden ? overrideValue : globalValue;
    if (!defaultValue) {
      return false;
    }
    return currentUrl !== defaultValue;
  }, [isOverridden, overrideValue, globalValue, defaultValue]);

  const handleResetClick = useCallback(() => {
    if (shouldClearOverride) {
      onClearOverride!();
    } else if (onReset) {
      onReset();
    }
  }, [shouldClearOverride, onClearOverride, onReset]);

  const showResetButton = useMemo(
    () => (isPageOverrideMode ? isOverridden : isCustomized),
    [isPageOverrideMode, isOverridden, isCustomized],
  );

  const handleImageRemove = useMemo(() => {
    if (!displayUrl) {
      return undefined;
    }
    return isPageOverrideMode ? onRemove : handleRemoveClick;
  }, [displayUrl, isPageOverrideMode, onRemove, handleRemoveClick]);

  const showPlaceholderReset = showResetButton && !displayUrl;

  const placeholderFooter = useMemo(() => {
    if (!showPlaceholderReset) {
      return undefined;
    }
    return (
      <ResetPlaceholder
        message="Hero media removed. No hero media will be shown."
        onClick={handleResetClick}
        dataAction={`${dataAction}_reset`}
      />
    );
  }, [showPlaceholderReset, handleResetClick, dataAction]);

  const uploaderConfig = useMemo(() => {
    return {
      title: 'Click to upload or drag media',
      helperText: `SVG, PNG, JPG or GIF (max. ${MAX_IMAGE_SIZE_BYTES_MB}MB)`,
      accept: VISUAL_MEDIA_TYPES,
      maxSize: MAX_IMAGE_SIZE_BYTES,
      height: 230,
      fileUrl: displayUrl || undefined,
      onDrop: handleDrop,
      onRemove: handleImageRemove,
      placeholderFooter,
      dataAction,
    };
  }, [displayUrl, handleDrop, handleImageRemove, placeholderFooter, dataAction]);

  return (
    <>
      <FormLabel label={label} />
      <ImageUploader {...uploaderConfig} />
    </>
  );
};
